/**
 * SchemaPreview Component
 * 
 * Displays JSON schemas (input/output) in a readable, formatted way.
 * Shows property types, descriptions, required fields, and defaults.
 */

import React from 'react';
import { JSONSchema } from '@simcheck/shared-types';

interface SchemaPreviewProps {
  schema?: JSONSchema;
  title?: string;
  emptyMessage?: string;
}

/**
 * Get icon for property type
 */
const getTypeIcon = (type: string): string => {
  const icons: Record<string, string> = {
    string: '📝',
    number: '🔢',
    boolean: '✓',
    array: '📋',
    object: '📦',
  };
  return icons[type] || '❓';
};

/**
 * Get color for property type
 */
const getTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    string: 'text-accent-blue',
    number: 'text-status-success',
    boolean: 'text-category-diagnostics',
    array: 'text-category-sms',
    object: 'text-category-voice',
  };
  return colors[type] || 'text-muted-foreground';
};

export const SchemaPreview: React.FC<SchemaPreviewProps> = ({
  schema,
  title = 'Schema',
  emptyMessage = 'No schema defined',
}) => {
  if (!schema || !schema.properties || Object.keys(schema.properties).length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-4">
        <h3 className="text-sm font-semibold text-muted-foreground mb-2">
          {title}
        </h3>
        <p className="text-sm text-muted-foreground/70 italic">
          {emptyMessage}
        </p>
      </div>
    );
  }

  const properties = schema.properties;
  const required = schema.required || [];

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <h3 className="text-sm font-semibold text-foreground mb-3">
        {title}
      </h3>

      <div className="space-y-3">
        {Object.entries(properties).map(([name, prop]) => (
          <div
            key={name}
            className="border-l-2 border-border pl-3 py-1"
          >
            <div className="flex items-start gap-2">
              <span className="text-lg leading-none">{getTypeIcon(prop.type)}</span>
              <div className="flex-1 min-w-0">
                {/* Property Name */}
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-sm font-mono font-semibold text-foreground">
                    {name}
                  </code>
                  
                  {/* Type Badge */}
                  <span
                    className={`text-xs font-mono px-1.5 py-0.5 rounded ${getTypeColor(
                      prop.type
                    )}`}
                  >
                    {prop.type}
                  </span>

                  {/* Required Badge */}
                  {required.includes(name) && (
                    <span className="text-xs font-semibold px-1.5 py-0.5 bg-status-error-bg text-status-error rounded">
                      required
                    </span>
                  )}
                </div>

                {/* Description */}
                {prop.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {prop.description}
                  </p>
                )}

                {/* Default Value */}
                {prop.default !== undefined && (
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    <span className="font-semibold">Default:</span>{' '}
                    <code className="bg-muted px-1 py-0.5 rounded">
                      {JSON.stringify(prop.default)}
                    </code>
                  </p>
                )}

                {/* Enum Values */}
                {prop.enum && prop.enum.length > 0 && (
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    <span className="font-semibold">Options:</span>{' '}
                    {prop.enum.map((value, i) => (
                      <span key={i}>
                        <code className="bg-muted px-1 py-0.5 rounded">
                          {JSON.stringify(value)}
                        </code>
                        {i < prop.enum!.length - 1 && ', '}
                      </span>
                    ))}
                  </p>
                )}

                {/* Array Items Type */}
                {prop.type === 'array' && prop.items && (
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    <span className="font-semibold">Items:</span>{' '}
                    <span className={getTypeColor(prop.items.type)}>
                      {prop.items.type}
                    </span>
                  </p>
                )}

                {/* Nested Object Properties */}
                {prop.type === 'object' && prop.properties && (
                  <div className="mt-2 ml-4 space-y-2 border-l-2 border-border dark:border-border-subtle pl-3">
                    {Object.entries(prop.properties).map(([nestedName, nestedProp]) => (
                      <div key={nestedName} className="text-xs">
                        <code className="font-mono font-semibold text-foreground">
                          {nestedName}
                        </code>
                        {': '}
                        <span className={getTypeColor(nestedProp.type)}>
                          {nestedProp.type}
                        </span>
                        {nestedProp.description && (
                          <span className="text-muted-foreground/70">
                            {' - '}
                            {nestedProp.description}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Info */}
      {schema.additionalProperties !== undefined && (
        <div className="mt-3 pt-3 border-t border-border dark:border-border-subtle">
          <p className="text-xs text-muted-foreground/70">
            <span className="font-semibold">Additional properties:</span>{' '}
            {schema.additionalProperties ? 'allowed' : 'not allowed'}
          </p>
        </div>
      )}
    </div>
  );
};

export default SchemaPreview;


